import filter from 'lodash/filter.js';
import some from 'lodash/some.js';
import { useI18n } from 'vue-i18n';

import File from '@/components/activity-types/course-activity-types/activities/File.js';
import { FILE_UPLOAD_CONTEXT, FILE_UPLOAD_REJECTION_REASON } from '@/constants/fileUploadRejection.js';
import { getMaxUploadFileSizeBytes, initialState } from '@/Helpers.js';
import { reportFileUploadRejections } from '@/utils/reportFileUploadRejection.js';

export default {
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },
    methods: {
        filesAreValid(files) {
            return this.fileSizesAreValid(files);
        },

        alertIfOverMaxFiles(files, maxFiles) {
            if (files.length > maxFiles) {
                this.$snackbar.open({
                    message: this.translate('max_files_reached', {
                        max: maxFiles,
                        files: this.translate('file', maxFiles),
                    }),
                });
            }
        },

        resolveMaxUploadFileSize() {
            // Prefer the app-wide globals when present; fall back so Options API
            // mixins still enforce the display limit if globals did not bind.
            if (Number.isFinite(this.globalMaxFileSize) && this.globalMaxFileSize > 0) {
                return {
                    maxSize: this.globalMaxFileSize,
                    maxLabel: this.globalMaxFileSizeLabel || initialState('MaxFileSizeDisplay') || '20 MB',
                };
            }

            const maxLabel = initialState('MaxFileSizeDisplay') || '20 MB';

            return {
                maxSize: getMaxUploadFileSizeBytes(maxLabel),
                maxLabel,
            };
        },

        /**
         * Rejected uploads are logged under the consumer's `uploadContext` (a prop or data field),
         * or as unspecified when the consumer does not define one.
         */
        fileSizesAreValid(files) {
            const { maxSize, maxLabel } = this.resolveMaxUploadFileSize();

            const sizesAreValid = !some(files, file => {
                if (file.size > maxSize) {
                    this.$snackbar.open({
                        type: 'is-danger',
                        position: 'is-bottom-left',
                        queue: false,
                        message: this.translate('file_size_too_large', {
                            max: maxLabel,
                            name: file.name,
                        }),
                        duration: 5000,
                    });

                    return true;
                }

                return false;
            });

            if (!sizesAreValid) {
                reportFileUploadRejections(
                    this.uploadContext || FILE_UPLOAD_CONTEXT.UNSPECIFIED,
                    filter(files, file => file.size > maxSize).map(file => ({
                        file,
                        reason: FILE_UPLOAD_REJECTION_REASON.FILE_TOO_LARGE,
                        maxFileSizeBytes: maxSize,
                    }))
                );
            }

            return sizesAreValid;
        },

        reduceFilesToMax(files, maxFiles) {
            return files
                .map(file => {
                    return file instanceof File ? file : new File(file);
                })
                .slice(-1 * maxFiles);
        },
    },
};
